// src/components/admin/CourseManagement.jsx v2

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../common/ConfirmationModal';
import NotificationModal from '../common/NotificationModal';
import CourseFormModal from './CourseFormModalTabs';

// Ícones (Assumindo que você tem bibliotecas como heroicons instaladas)
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const PencilIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m-4-6h4m4-6H5" /></svg>;

function CourseManagement() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    const [showCourseFormModal, setShowCourseFormModal] = useState(false);
    const [courseToEdit, setCourseToEdit] = useState(null);

    // 🔵 Degustação
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [coursePreviewEdit, setCoursePreviewEdit] = useState(null);
    const [previewSeconds, setPreviewSeconds] = useState(20);

    const canManageCourses = user && (user.role === 'admin' || user.role === 'supervisor' || user.role === 'professor');
    const canDelete = user && user.role === 'admin';
    const API_COURSE_URL = '/courses';

    const closeNotification = () => setNotification({ ...notification, show: false });

    const fetchCourses = useCallback(async () => {
        if (!canManageCourses) {
            setLoading(false);
            setError("Você não tem permissão para ver a gestão de cursos.");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await api.get(API_COURSE_URL);
            setCourses(response.data.courses);
        } catch (err) {
            console.error("Erro ao buscar cursos:", err.response?.data?.message || err.message);
            const errorMessage = err.response?.data?.message || 'Falha ao carregar a lista de cursos.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [canManageCourses]);

    const handleCreateNew = () => {
        if (!canManageCourses) return;
        setCourseToEdit(null);
        setShowCourseFormModal(true);
    };

    const handleEditDetails = (course) => {
        if (!canManageCourses) return;
        setCourseToEdit(course);
        setShowCourseFormModal(true);
    };

    // Conteúdo (mantido, mas não usado na tabela)
    const handleEditContent = (course) => {
        if (!canManageCourses) return;
        navigate(`/admin/cursos/editor/${course.id}`);
    };

    // Preview do aluno
    const handlePreview = (course) => {
        navigate(`/cursos/${course.id}?preview=true`);
    };

    // 🔵 Abrir modal de tempo de degustação
    const handleEditPreviewTime = (course) => {
        setCoursePreviewEdit(course);
        setPreviewSeconds(20); // padrão
        setShowPreviewModal(true);
    };

    const handleCourseSaved = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const closeCourseFormModal = () => {
        setShowCourseFormModal(false);
        setCourseToEdit(null);
    };

    const openDeleteConfirmation = (courseId) => {
        if (!canDelete) {
            return setNotification({
                show: true,
                message: "Acesso Negado. Apenas Administradores podem deletar.",
                type: 'error'
            });
        }
        setCourseToDelete(courseId);
        setShowConfirmModal(true);
    };

    const handleDelete = async () => {
        if (!courseToDelete) return;
        setShowConfirmModal(false);

        try {
            await api.delete(`${API_COURSE_URL}/${courseToDelete}`);
            setNotification({
                show: true,
                message: `Curso ID ${courseToDelete} excluído com sucesso!`,
                type: 'success'
            });
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Falha ao excluir o curso.';
            setNotification({
                show: true,
                message: `Erro ao excluir: ${errorMessage}`,
                type: 'error'
            });
        } finally {
            setCourseToDelete(null);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, [refreshTrigger, fetchCourses]);

    if (loading) return <div className="text-center p-4">Carregando Cursos...</div>;

    /* ======================================================
    🔵 Modal de TEMPO DE DEGUSTAÇÃO (Corrigido)
    ======================================================= */
    const PreviewTimeModal = () => {
        if (!showPreviewModal || !coursePreviewEdit) return null;

        const save = async () => {
            // Revertendo a lógica do frontend para que o backend determine o lessonId.
            // Se você não pode corrigir o backend, use: const lessonIdParaPreview = 1;
            const lessonIdParaPreview = 1; // ⚠️ PLACHOLDER: Use o ID real se souber

            try {
                // Mantemos a rota curta e adicionamos o lessonId
                await api.put(`/courses/${coursePreviewEdit.id}/preview-time`, {
                    lessonId: lessonIdParaPreview,
                    preview_seconds: Number(previewSeconds)
                });

                setNotification({
                    show: true,
                    message: "Tempo de degustação atualizado!",
                    type: "success"
                });

                setShowPreviewModal(false);
            } catch (err) {
                console.error(err);
                const errorMessage = err.response?.data?.message || 'Erro desconhecido ao atualizar preview.';
                setNotification({
                    show: true,
                    message: `Erro ao atualizar tempo de degustação: ${errorMessage}`,
                    type: "error"
                });
            }
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg w-96 shadow-xl">
                    <h2 className="text-xl font-bold mb-4">Tempo de Degustação</h2>
                    <p className="mb-4 text-sm text-gray-600">Defina o tempo de visualização do curso: **{coursePreviewEdit?.title}**.</p>
                    
                    <label className="text-sm font-medium">Tempo (segundos):</label>
                    <input
                        type="number"
                        min={5}
                        value={previewSeconds}
                        onChange={(e) => setPreviewSeconds(e.target.value)}
                        className="w-full border border-gray-300 p-2 rounded mt-1 focus:border-indigo-500"
                    />

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={() => setShowPreviewModal(false)}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={save}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition"
                        >
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ======================================================
    🔵 RENDER FINAL
    ======================================================= */

    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 lg:col-span-1">

            <PreviewTimeModal />

            <div className="flex justify-between items-center mb-6 border-b pb-3">
                <h3 className="text-2xl font-bold text-gray-800">📚 Gestão de Conteúdo (Cursos)</h3>
                {canManageCourses && (
                    <button
                        onClick={handleCreateNew}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition shadow-md"
                    >
                        + Novo Curso
                    </button>
                )}
            </div>

            {error && <div className="bg-red-100 text-red-800 p-3 rounded mb-4 text-sm font-medium">{error}</div>}

            <p className="text-sm mb-4 text-gray-500">Total de Cursos: <span className="font-semibold text-gray-700">{courses.length}</span></p>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider w-1/4">Título</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider w-1/12">Módulos</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider w-1/6">Status</th>
                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-1/2">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {courses.map((course) => (
                            <tr key={course.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                    {course.title}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                                    {/* Exibindo modulesCount (assumindo que o backend corrigido o enviará) */}
                                    {course.modulesCount || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${course.status === 'Publicado'
                                            ? 'bg-green-100 text-green-800'
                                            : course.status === 'Rascunho'
                                            ? 'bg-gray-200 text-gray-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                    >
                                        {course.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                    {canManageCourses && (
                                        <div className="flex items-center space-x-3 justify-center">

                                            <button
                                                onClick={() => handlePreview(course)}
                                                title="Visualizar no site"
                                                className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                                            >
                                                <EyeIcon />
                                            </button>

                                            <button
                                                onClick={() => handleEditDetails(course)}
                                                title="Editar Detalhes (Metadados)"
                                                className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-50"
                                            >
                                                <PencilIcon />
                                            </button>

                                            {/* Nota: Se você tivesse uma rota para o editor de conteúdo, ela seria: */}
                                            {/* <button
                                                onClick={() => handleEditContent(course)}
                                                title="Editar Conteúdo (Módulos/Aulas)"
                                                className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
                                            >
                                                <PencilIcon />
                                            </button> */}

                                            <button
                                                onClick={() => handleEditPreviewTime(course)}
                                                title="Definir Tempo de Degustação"
                                                className="text-yellow-600 hover:text-yellow-800 p-1 rounded-full hover:bg-yellow-50"
                                            >
                                                <ClockIcon />
                                            </button>

                                            {canDelete && (
                                                <button
                                                    onClick={() => openDeleteConfirmation(course.id)}
                                                    title="Deletar Curso"
                                                    className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}

                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <CourseFormModal
                show={showCourseFormModal}
                onClose={closeCourseFormModal}
                courseData={courseToEdit}
                onCourseSaved={handleCourseSaved}
                setNotification={setNotification}
            />

            <ConfirmationModal
                show={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleDelete}
                title="Confirmar Deleção"
                message={`Tem certeza que deseja DELETAR o curso ID ${courseToDelete}? Esta ação é irreversível e excluirá todos os módulos e aulas relacionados.`}
                confirmText="Deletar"
            />

            <NotificationModal
                show={notification.show}
                onClose={closeNotification}
                message={notification.message}
                type={notification.type}
            />
        </div>
    );
}

export default CourseManagement;